import { useEffect, useState, useContext, JSX } from "react";
import { capitalize } from "lodash-es";
import { useDispatch } from "react-redux";
import { useRouter } from "next/router";
import { Heading } from "@otrium/atoms";
import { Box, Flex } from "@otrium/core";
import { Trans, useLingui } from "@lingui/react";
import { t } from "@lingui/macro";
import { Content, ContentMobileFullscreen } from "src/atoms/Content";
import PopularCategories from "src/organisms/PopularCategories";
import { AppCtx } from "src/contexts/app.context";
import { Gender, setGender } from "src/modules/page";
import { localeFix } from "src/utils/genderLocale/genderLocale";
import { getI18nRoute } from "src/utils/i18nRoutes";
import { PageHead } from "src/molecules/PageHead";
import { PopularBrands } from "src/organisms/PopularBrands";
import { Breadcrumbs, BreadcrumbsItem } from "src/molecules/Breadcrumbs";

const CategoryViewBreadcrumbs = ({ genderId }: { genderId: string }) => {
  const { locale } = useContext(AppCtx);

  const items: BreadcrumbsItem[] = [
    {
      name: <Trans id="Home" />,
      href: getI18nRoute({ route: `/${genderId}`, locale }),
    },
    {
      name: <Trans id={capitalize(genderId)} />,
      href: getI18nRoute({ route: `/${genderId}`, locale }),
    },
    { name: <Trans id="All categories" /> },
  ];

  return <Breadcrumbs items={items} />;
};

export default function CategoryView({
  isKidsPage = false,
}: {
  isKidsPage?: boolean;
}): JSX.Element {
  const { i18n } = useLingui();
  const [isMounted, setIsMounted] = useState(false);
  const router = useRouter();
  const { slug } = router.query;
  const localeGenderId = localeFix({
    gender: isKidsPage ? "kids" : (slug as string),
  });

  const reduxDispatch = useDispatch();

  // NOTE: Source: https://github.com/vercel/next.js/issues/2473#issuecomment-587551234
  useEffect(() => setIsMounted(true), []);

  useEffect(() => {
    reduxDispatch(setGender(localeGenderId));
  }, [localeGenderId, reduxDispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const genderToUpper = (str: string | string[]): string =>
    Array.isArray(str)
      ? str[0].charAt(0).toUpperCase()
      : str?.charAt(0).toUpperCase();

  return (
    <>
      <Content as="main">
        <PageHead
          title={t(i18n)`${
            (slug && genderToUpper(slug)) as string
          } | Product categories | Otrium`}
          deeplink={`otrium://product-category/${localeGenderId}`}
        />
        <CategoryViewBreadcrumbs genderId={slug as string} />
        <header>
          <Heading
            data-testid="page-title"
            as="h1"
            variant="h2"
            fontSize={["20px", "20px", "20px", "28px", "28px", "28px"]}
            mb={3}
            sx={{
              textTransform: "uppercase",
              letterSpacing: "1.8px",
              fontWeight: "700",
              color: "black",
            }}
          >
            <Trans id="All categories" />
          </Heading>
        </header>
        <Flex
          flex={1}
          pb={3}
          sx={{ alignItems: "flex-start", position: "relative" }}
        >
          <PopularCategories />
        </Flex>
      </Content>
      {isMounted && slug && (
        <Box bg="tone.marble" py={5} sx={{ overflow: "hidden" }}>
          <ContentMobileFullscreen>
            <PopularBrands
              localeGenderId={localeGenderId}
              genderId={(slug as Gender) || "women"}
              pageName="category_product_listing_page"
            />
          </ContentMobileFullscreen>
        </Box>
      )}
    </>
  );
}
